"use client";

import { useState, useMemo, useEffect, Fragment } from "react";
import CommonHeader from "./common/CommonHeader";
import { ActivitySearchBar } from "./activities/ActivitySearchBar";
import { ActivityFilter } from "./activities/ActivityFilter";
import { activityService } from "@/services/activityService";
import { StaffActivity } from "@/types/activity.types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { ArrowUpDown, ChevronDown, ChevronRight, Clock } from "lucide-react";
import { formatRelativeTime } from "@/lib/utils";

interface TimeTrackingSession {
  id: string;
  recordId: string;
  staffName: string | null;
  staffEmail: string | null;
  startedAt: string;
  endedAt: string | null;
  startActivity: StaffActivity;
  endActivity: StaffActivity | null;
  /** used for sorting */
  createdAt: string;
}

function groupTimeTrackingSessions(activities: StaffActivity[]): TimeTrackingSession[] {
  const sessionMap = new Map<string, { start?: StaffActivity; end?: StaffActivity }>();

  for (const a of activities) {
    if (a.module !== "STAFF_TIME_TRACKING") continue;
    if (!sessionMap.has(a.recordId)) sessionMap.set(a.recordId, {});
    const entry = sessionMap.get(a.recordId)!;
    if (a.description === "Started working hours tracking") {
      entry.start = a;
    } else if (a.description === "Ended working hours tracking") {
      entry.end = a;
    }
  }

  const sessions: TimeTrackingSession[] = [];
  for (const [recordId, { start, end }] of sessionMap) {
    if (!start) continue;
    sessions.push({
      id: recordId,
      recordId,
      staffName: start.staffName,
      staffEmail: start.staffEmail,
      startedAt: start.createdAt,
      endedAt: end?.createdAt ?? null,
      startActivity: start,
      endActivity: end ?? null,
      createdAt: start.createdAt,
    });
  }
  return sessions;
}

function formatDuration(startedAt: string, endedAt: string): string {
  const diffMs = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  if (diffMs < 0) return "—";
  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

const actionColors: Record<string, string> = {
  CREATE: "bg-green-50 text-green-700 border-green-100",
  UPDATE: "bg-blue-50 text-blue-700 border-blue-100",
  DELETE: "bg-red-50 text-red-700 border-red-100",
  LOGIN: "bg-emerald-50 text-emerald-700 border-emerald-100",
  LOGOUT: "bg-amber-50 text-amber-700 border-amber-100",
};

const moduleColors: Record<string, string> = {
  STAFF: "bg-orange-50 text-orange-700 border-orange-100",
  STAFF_TIME_TRACKING: "bg-yellow-50 text-yellow-700 border-yellow-100",
  APPOINTMENT: "bg-indigo-50 text-indigo-700 border-indigo-100",
};

function parseActivityData(data: unknown): Record<string, unknown> | null {
  if (!data || data === "null" || data === null) return null;
  if (typeof data === "string") {
    try { return JSON.parse(data); } catch { return null; }
  }
  if (typeof data === "object") return data as Record<string, unknown>;
  return null;
}

function ActivityDataView({ oldData, newData, action }: { oldData: unknown; newData: unknown; action: string }) {
  const parsedOld = parseActivityData(oldData);
  const parsedNew = parseActivityData(newData);

  if (action === "CREATE" && parsedNew) {
    return (
      <div className="space-y-2">
        <div className="text-xs font-semibold text-green-700 mb-2">New Data:</div>
        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(parsedNew, null, 2)}</pre>
        </div>
      </div>
    );
  }
  if (action === "DELETE" && parsedOld) {
    return (
      <div className="space-y-2">
        <div className="text-xs font-semibold text-red-700 mb-2">Deleted Data:</div>
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(parsedOld, null, 2)}</pre>
        </div>
      </div>
    );
  }
  if (action === "UPDATE") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-700 mb-2">Old Data:</div>
          {parsedOld ? (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(parsedOld, null, 2)}</pre>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-muted-foreground">No previous data</div>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-xs font-semibold text-blue-700 mb-2">New Data:</div>
          {parsedNew ? (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <pre className="text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(parsedNew, null, 2)}</pre>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-muted-foreground">No new data</div>
          )}
        </div>
      </div>
    );
  }
  return null;
}

// Unified row type used in the table — either a raw activity or a grouped session
type TableRow =
  | { kind: "activity"; data: StaffActivity }
  | { kind: "session"; data: TimeTrackingSession };

export function StaffActivityManagement() {
  const [activities, setActivities] = useState<StaffActivity[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAction, setFilterAction] = useState<string>("all");
  const [filterModule, setFilterModule] = useState<string>("all");
  const [sortConfig, setSortConfig] = useState<{
    key: "createdAt" | "action" | "module" | "description";
    direction: "asc" | "desc";
  }>({ key: "createdAt", direction: "desc" });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    activityService.getStaffActivities().then(setActivities);
  }, []);

  const filteredAndSorted = useMemo(() => {
    // Split into time-tracking vs other activities
    const timeTrackingActivities = activities.filter((a) => a.module === "STAFF_TIME_TRACKING");
    const otherActivities = activities.filter((a) => a.module !== "STAFF_TIME_TRACKING");

    // Group time tracking into sessions, treat others as flat rows
    const sessions = groupTimeTrackingSessions(timeTrackingActivities);

    // Build unified rows
    const rows: TableRow[] = [
      ...sessions.map((s): TableRow => ({ kind: "session", data: s })),
      ...otherActivities.map((a): TableRow => ({ kind: "activity", data: a })),
    ];

    // Filter
    const filtered = rows.filter((row) => {
      if (row.kind === "session") {
        const s = row.data;
        const matchesSearch =
          searchTerm === "" ||
          (s.staffName ?? "").toLowerCase().includes(searchTerm.toLowerCase());
        const matchesModule = filterModule === "all" || filterModule === "STAFF_TIME_TRACKING";
        // Sessions have both CREATE (start) and UPDATE (end) — skip action filter for sessions
        const matchesAction = filterAction === "all" || filterAction === "CREATE" || filterAction === "UPDATE";
        return matchesSearch && matchesModule && matchesAction;
      } else {
        const a = row.data;
        const matchesSearch =
          searchTerm === "" ||
          a.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          a.module.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (a.staffName ?? "").toLowerCase().includes(searchTerm.toLowerCase());
        const matchesAction = filterAction === "all" || a.action === filterAction;
        const matchesModule = filterModule === "all" || a.module === filterModule;
        return matchesSearch && matchesAction && matchesModule;
      }
    });

    // Sort by createdAt (primary sort key that makes sense across both types)
    filtered.sort((a, b) => {
      const aDate = new Date(a.data.createdAt).getTime();
      const bDate = new Date(b.data.createdAt).getTime();
      if (sortConfig.key === "createdAt") {
        return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
      }
      // For other sort keys fall back to date desc for sessions, field sort for activities
      if (a.kind === "activity" && b.kind === "activity") {
        let aVal: string | Date;
        let bVal: string | Date;
        switch (sortConfig.key) {
          case "action": aVal = a.data.action; bVal = b.data.action; break;
          case "module": aVal = a.data.module; bVal = b.data.module; break;
          case "description": aVal = a.data.description; bVal = b.data.description; break;
          default: return 0;
        }
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      }
      return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
    });

    return filtered;
  }, [activities, searchTerm, filterAction, filterModule, sortConfig]);

  const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated: TableRow[] = filteredAndSorted.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
  }, [filteredAndSorted.length, currentPage, totalPages]);

  const handleSort = (key: "createdAt" | "action" | "module" | "description") => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const hasData = (a: StaffActivity) =>
    Boolean(a.oldData && a.oldData !== "null") || Boolean(a.newData && a.newData !== "null");

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage <= 3) {
        for (let i = 2; i <= 4; i++) pages.push(i);
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push("ellipsis");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      <CommonHeader
        title="Staff Activity"
        description="Monitor all staff login, logout, and action history"
        showButton={false}
      />

      <div className="grid gap-4 md:grid-cols-3">
        <ActivitySearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
        <ActivityFilter
          filterAction={filterAction}
          filterModule={filterModule}
          onFilterActionChange={setFilterAction}
          onFilterModuleChange={setFilterModule}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">
              Staff Activities ({filteredAndSorted.length})
            </CardTitle>
            {filteredAndSorted.length > 0 && (
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1} - {Math.min(startIndex + itemsPerPage, filteredAndSorted.length)} of {filteredAndSorted.length}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b hover:bg-transparent">
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="text-xs font-medium">Staff</TableHead>
                  <TableHead className="text-xs font-medium">
                    <Button variant="ghost" size="sm" onClick={() => handleSort("action")} className="h-8 px-2">
                      Action <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-xs font-medium">
                    <Button variant="ghost" size="sm" onClick={() => handleSort("module")} className="h-8 px-2">
                      Module <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-xs font-medium">
                    <Button variant="ghost" size="sm" onClick={() => handleSort("description")} className="h-8 px-2">
                      Description <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-xs font-medium">
                    <Button variant="ghost" size="sm" onClick={() => handleSort("createdAt")} className="h-8 px-2">
                      Date <ArrowUpDown className="ml-1 h-3 w-3" />
                    </Button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No staff activities found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((row) => {
                    if (row.kind === "session") {
                      const s = row.data;
                      const isExpanded = expandedRows.has(s.id);
                      return (
                        <Fragment key={s.id}>
                          <TableRow className="border-b">
                            <TableCell className="w-10">
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleRow(s.id)}>
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </Button>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm font-medium">{s.staffName ?? "—"}</div>
                              {s.staffEmail && (
                                <div className="text-xs text-muted-foreground">{s.staffEmail}</div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3 text-yellow-600" />
                                <span className="text-xs font-medium text-yellow-700">SESSION</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-xs ${moduleColors["STAFF_TIME_TRACKING"]}`}>
                                STAFF_TIME_TRACKING
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className={`text-xs ${actionColors["CREATE"]}`}>START</Badge>
                                  <span className="text-xs">{formatDateTime(s.startedAt)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className={`text-xs ${s.endedAt ? actionColors["UPDATE"] : "bg-gray-50 text-gray-400 border-gray-100"}`}>END</Badge>
                                  <span className="text-xs">
                                    {s.endedAt ? formatDateTime(s.endedAt) : <span className="text-muted-foreground italic">In progress</span>}
                                  </span>
                                </div>
                                {s.endedAt && (
                                  <div className="text-xs text-muted-foreground">
                                    Duration: {formatDuration(s.startedAt, s.endedAt)}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              <div>{formatDateTime(s.startedAt)}</div>
                              <div className="text-xs text-muted-foreground">
                                {formatRelativeTime(new Date(s.startedAt))}
                              </div>
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={6} className="bg-muted/30 p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <div className="text-xs font-semibold text-green-700 mb-2">Session Start Data:</div>
                                    <ActivityDataView oldData={s.startActivity.oldData} newData={s.startActivity.newData} action="CREATE" />
                                  </div>
                                  {s.endActivity && (
                                    <div>
                                      <div className="text-xs font-semibold text-blue-700 mb-2">Session End Data:</div>
                                      <ActivityDataView oldData={s.endActivity.oldData} newData={s.endActivity.newData} action="UPDATE" />
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    }

                    // Regular (non-time-tracking) activity row
                    const activity = row.data;
                    const isExpanded = expandedRows.has(activity.id);
                    const showExpand = hasData(activity);
                    return (
                      <Fragment key={activity.id}>
                        <TableRow className="border-b">
                          <TableCell className="w-10">
                            {showExpand && (
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => toggleRow(activity.id)}>
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm font-medium">{activity.staffName ?? "—"}</div>
                            {activity.staffEmail && (
                              <div className="text-xs text-muted-foreground">{activity.staffEmail}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-xs ${actionColors[activity.action] ?? "bg-gray-50 text-gray-700 border-gray-100"}`}>
                              {activity.action}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-xs ${moduleColors[activity.module] ?? "bg-gray-50 text-gray-700 border-gray-100"}`}>
                              {activity.module}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{activity.description}</TableCell>
                          <TableCell className="text-sm">
                            <div>{new Date(activity.createdAt).toLocaleString("en-GB", {
                              day: "2-digit", month: "short", year: "numeric",
                              hour: "2-digit", minute: "2-digit", hour12: false,
                            })}</div>
                            <div className="text-xs text-muted-foreground">
                              {formatRelativeTime(new Date(activity.createdAt))}
                            </div>
                          </TableCell>
                        </TableRow>
                        {showExpand && isExpanded && (
                          <TableRow>
                            <TableCell colSpan={6} className="bg-muted/30 p-4">
                              <ActivityDataView oldData={activity.oldData} newData={activity.newData} action={activity.action} />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {filteredAndSorted.length > 0 && totalPages > 1 && (
            <div className="mt-4">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => { e.preventDefault(); handlePageChange(currentPage - 1); }}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  {getPageNumbers().map((page, idx) =>
                    page === "ellipsis" ? (
                      <PaginationItem key={`ellipsis-${idx}`}><PaginationEllipsis /></PaginationItem>
                    ) : (
                      <PaginationItem key={page}>
                        <PaginationLink
                          href="#"
                          onClick={(e) => { e.preventDefault(); handlePageChange(page); }}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => { e.preventDefault(); handlePageChange(currentPage + 1); }}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
